<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" class="fancy-formatting-container">
        <aside class="fancy-image">
          <figure>
            <img
              :alt="currentRound.contentImage?.image.description"
              :src="currentRound.contentImage?.image.src"
            />
          </figure>
        </aside>

        <section class="fancy-formatting-instructions-container" :class="{ isMobile }">
          <span class="round-instructions">{{ currentRound.instructions }}</span>

          <div class="format-section">
            <div class="format-section-background" :style="svgElements.buttonsContainer">
              <div class="format-buttons-container">
                <div class="title">
                  <span>Text Formatting Tools</span>
                </div>

                <div class="format-buttons">
                  <TextFormattingButtons :options="optionsTextFormattingButtons">
                    <li>
                      <button
                        class="solve-all-button pure-button"
                        tabindex="0"
                        @click="handleSolveAll"
                      >
                        <span>Solve All</span>
                      </button>
                    </li>
                  </TextFormattingButtons>
                </div>
              </div>
            </div>
          </div>

          <text-display-list text-size="smaller" class="format-text-display">
            <li
              class="typing-section"
              :class="{ selectable: typingDone }"
              @mouseup="setFirstSelectedWordElement"
              @touchend="setFirstSelectedWordElement"
            >
              <blinking-cursor-text
                :text="currentStep.answer"
                :text-step="0"
                :text-selectable-after-typing="true"
              ></blinking-cursor-text>
            </li>
          </text-display-list>
        </section>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

import TextFormattingButtons from 'components/buttons/TextFormattingButtons.vue';

import buttonsContainer from 'assets/svg/text-formatting-section/text-formatting-bar-empty.svg';

export default {
  components: {
    ActivityStage,
    TextDisplayList,
    TextFormattingButtons,
    BlinkingCursorText
  },
  mixins: [ActivityManager, TypingActivityHelper, AssetLoader, ErrorLogger],
  data() {
    return {
      activityName: 'FancyFormatting',
      svgElements: {
        buttonsContainer: {
          backgroundImage: `url('data:image/svg+xml;utf8,${buttonsContainer}')`,
          backgroundRepeat: 'no-repeat'
        }
      },
      textManualSelection: '',
      typingDone: false,
      firstSelectedWordElement: '',
      requiredSelectionAmount: 0,
      countCorrectSelection: 0,
      currentIndex: 0
    };
  },
  mounted() {
    if (this.isMobile) {
      document.addEventListener('touchend', this.setFirstSelectedWordElement);
    }
  },
  beforeDestroy() {
    TweenMax.killAll();

    if (this.isMobile) {
      document.removeEventListener('touchend', this.setFirstSelectedWordElement);
    }
  },
  computed: {
    optionsTextFormattingButtons() {
      if (!this.currentStep) {
        return null;
      }
      return {
        isBoldDisabled: this.currentStep.italic || this.currentStep.underline,
        boldCallback: () => this.formatText('bold'),
        isItalicDisabled: this.currentStep.bold || this.currentStep.underline,
        italicCallback: () => this.formatText('italic'),
        isUnderlineDisabled: this.currentStep.italic || this.currentStep.bold,
        underlineCallback: () => this.formatText('underline')
      };
    },
    textToBeSelected() {
      if (!this.currentStep) {
        return '';
      }

      return (this.currentStep.bold || this.currentStep.italic || this.currentStep.underline || '')
        .toLowerCase()
        .trim();
    },
    wordsToBeSelected() {
      return this.textToBeSelected.split(' ').map(word => word.trim());
    }
  },
  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ contentImage }) => {
        this.queueImage(contentImage.image.src);
      });

      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Type the text and follow the instructions to format the word or phrase.',
          voice: `${this.voiceInstructionsPrefix}/fancyformatting-ma-1.mp3`
        }
      };
    },
    getKeyboardConfig() {
      return {
        hideNumberRow: !this.activityData.keyboard.showNumbers
      };
    },
    startActivity() {
      const timeline = new TimelineLite();
      const self = this;

      timeline
        .call(this.setupNewRound)
        .call(this.enableKeyboard)
        .call(this.errorLoggerStart);

      this.$nextTick(this.setActivityContainerHeight);

      TweenMax.delayedCall(0.2, () => {
        TweenMax.to('.solve-all-button', 0, { autoAlpha: 0 });
        TweenMax.delayedCall(0.5, self.tweenActivityStageChildrenVisibility.bind(self, true));
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();
      this.currentIndex += 1;

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.shouldThrottle()) {
        this.throttleKeyboard = true;
        setTimeout(() => {
          this.throttleKeyboard = false;
        }, 250);
      }

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.typingDone = true;

        this.startSolveAllCountdown();
      }
    },

    tweenActivityStageChildrenVisibility(show) {
      const leftSide = document.getElementsByClassName('fancy-image')[0];
      const roundInstructionsSection = document.getElementsByClassName('round-instructions')[0];
      const formatSection = document.getElementsByClassName('format-buttons')[0];
      const typingSection = document.getElementsByClassName('typing-section')[0];

      TweenMax.staggerTo(
        [leftSide, roundInstructionsSection, formatSection, typingSection],
        0.25,
        {
          autoAlpha: show ? 1 : 0
        },
        Math.random() / 10
      );
    },

    blurInput() {
      if (
        (this.usingExternalKeyboard && document.activeElement.nodeName === 'INPUT') ||
        this.ipadWithoutExternalKeyboard
      ) {
        this.typingDone = false;

        TweenMax.delayedCall(0, () => {
          TweenMax.to('.solve-all-button', 1, { autoAlpha: 1 });
        });
      }
    },

    handleStepEnd() {
      this.sndBell();
      this.finishStep();
    },

    finishStep() {
      this.completeStep();
      if (!this.isEndOfRound) {
        this.setCurrentStep();
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (this.isEndOfActivity) {
        timeline.delay(1).call(this.completeActivity);
      } else {
        timeline
          .delay(1)
          .to('.activity-stage', 0.5, { opacity: 0 })
          .call(this.setupNewRound)
          .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
      }
    },

    startSolveAllCountdown() {
      const interval = this.isMobile ? 10 : 30;
      TweenMax.delayedCall(interval, () => {
        TweenMax.to('.solve-all-button', 1, { autoAlpha: 1 });
      });
    },

    setFirstSelectedWordElement() {
      if (!this.typingDone) {
        return;
      }

      const selection = document.getSelection();

      const selectionArray = selection
        .toString()
        .trim()
        .toLowerCase()
        .split(' ')
        .filter(i => i)
        .map(i => this.removeNewLineCharacters(i).trim())
        .join(' ');

      this.textManualSelection = selectionArray;

      let firstSelectedWordElement = selection.getRangeAt(0).startContainer.parentNode.parentNode;
      if (selection.toString().startsWith(' ')) {
        firstSelectedWordElement = firstSelectedWordElement.nextSibling;
      }
      // eslint-disable-next-line
      this.requiredSelectionAmount = this.countStringOccurence(
        this.textToBeSelected,
        this.currentStep.answer
      );
      this.firstSelectedWordElement = firstSelectedWordElement;
    },

    countStringOccurence(stringToFind, sourceString) {
      const searchString = (stringToFind || '').toLowerCase();
      const allParagraph = (sourceString || '').toLowerCase();
      let count = 0;
      let index = allParagraph.indexOf(searchString);

      while (index !== -1) {
        count += 1;
        index = allParagraph.indexOf(searchString, (index += 1));
      }

      return count;
    },

    isTextAlreadyFormatted(arrayDOMElements) {
      const result = arrayDOMElements.find(domElement =>
        domElement.className.includes('formatted')
      );

      return result !== undefined;
    },

    formatText(type) {
      if (!this.textManualSelection) {
        return;
      }

      const currentElementSelection = this.getElementSiblingSequenceForTextPhrase(
        this.firstSelectedWordElement,
        this.textManualSelection.length
      );

      // skip if text's been already formatted
      if (this.isTextAlreadyFormatted(currentElementSelection)) {
        return;
      }

      if (
        this.textManualSelection === this.textToBeSelected ||
        this.textManualSelection === `${this.textToBeSelected}.`
      ) {
        this.handleCorrectSelection(type, currentElementSelection);
      } else {
        this.sndError();
      }
    },

    handleCorrectSelection(type, currentElementSelection) {
      if (
        this.textManualSelection === this.textToBeSelected ||
        this.textManualSelection === `${this.textToBeSelected}.`
      ) {
        this.countCorrectSelection += 1;
      }

      currentElementSelection.forEach(el => {
        const characters = ['"', "'", ' ', ',', '.', '!', '?'];
        const spans = el.querySelectorAll('span');
        el.classList.add('formatted');
        spans.forEach(span => {
          const text = span.innerText;
          if (!characters.includes(text)) {
            span.classList.add(type);
          }
        });
      });

      if (this.countCorrectSelection === this.requiredSelectionAmount) {
        this.textManualSelection = '';

        this.sndBell();

        if (this.isEndOfCurrentSentence) {
          this.handleStepEnd();
        }

        if (this.isEndOfRound) {
          this.handleRoundEnd();
        }

        this.currentIndex = 0;
      }

      this.resetDocumentSelection();
    },

    getElementSiblingSequenceForTextPhrase(targetElement, textLength) {
      let currentElement = targetElement;
      let counter =
        currentElement && currentElement.innerText
          ? this.removeNewLineCharacters(currentElement.innerText).length
          : 0;

      const siblingSequence = [targetElement];

      while (counter < textLength) {
        if (!currentElement || !currentElement.nextSibling) {
          break;
        }

        counter +=
          currentElement.nextSibling && currentElement.nextSibling.innerText
            ? this.removeNewLineCharacters(currentElement.nextSibling.innerText).length
            : 0;

        siblingSequence.push(currentElement.nextSibling);

        currentElement = currentElement.nextSibling;
      }

      return siblingSequence;
    },

    getSpansThatCouldContainWordsToBeSelected() {
      const typingSection = document.getElementsByClassName('typing-section')[0];
      const displayPararaph = typingSection.getElementsByTagName('p')[0];
      const displayWordsSpanList = displayPararaph.children;
      const {
        currentStep: { bold, italic, underline }
      } = this;

      const section = bold || italic || underline;

      const isSequence = section.split(' ').length > 1;
      const data = [...displayWordsSpanList]
        .reduce((accumulator, displayWordSpan) => {
          if (!displayWordSpan || !displayWordSpan.innerText) {
            return accumulator;
          }

          let displayWord = this.getSpanInnerText(displayWordSpan).trim();
          if (!isSequence) {
            // stripe out other characters if it's word only
            displayWord = displayWord.replace(/[,!?. "']/g, '').trim();
          }

          let wordIndex = this.wordsToBeSelected.indexOf(displayWord);
          if (wordIndex === -1 && displayWord.endsWith('.')) {
            const displayWordWithoutDot = displayWord.substring(0, displayWord.length - 1);
            wordIndex = this.wordsToBeSelected.indexOf(displayWordWithoutDot);
          }

          if (wordIndex !== -1) {
            accumulator.push(displayWordSpan);
          }

          return accumulator;
        }, [])
        .filter(({ innerText }) => innerText);

      return data;
    },

    getAllSpansWithWords() {
      let spans = this.getSpansThatCouldContainWordsToBeSelected();

      let [firstWord] = this.wordsToBeSelected;
      let indexOfSpanWithFirstWord = null;
      const spansWithWords = [];
      const callbackFilterBetweenStartAndEndIndex = (x, index) =>
        index > indexOfSpanWithFirstWord + this.wordsToBeSelected.length - 1;
      const callbackFilterFromStartIndex = (x, index) => index > indexOfSpanWithFirstWord;

      do {
        firstWord = firstWord.replace(/[,!?. "']/g, '').trim();
        // eslint-disable-next-line no-loop-func
        indexOfSpanWithFirstWord = spans.findIndex(
          span =>
            firstWord ===
            this.getSpanInnerText(span)
              .replace(/[,!?. "']/g, '')
              .trim()
        );

        if (indexOfSpanWithFirstWord === -1) {
          break;
        }

        let isCompleteSequence = true;
        for (let i = 0; i < this.wordsToBeSelected.length; i += 1) {
          const correspondingWord = this.wordsToBeSelected[i].replace(/[,!?. "']/g, '').trim();
          const matchingSpanKey = this.getSpanInnerText(spans[i + indexOfSpanWithFirstWord])
            .replace(/[,!?. "']/g, '')
            .trim();

          if (
            matchingSpanKey !== `${correspondingWord}` &&
            matchingSpanKey !== `${correspondingWord}.`
          ) {
            isCompleteSequence = false;
            break;
          }
        }

        if (isCompleteSequence) {
          for (let i = 0; i < this.wordsToBeSelected.length; i += 1) {
            spansWithWords.push(spans[indexOfSpanWithFirstWord + i]);
          }
          spans = spans.filter(callbackFilterBetweenStartAndEndIndex);
        } else {
          spans = spans.filter(callbackFilterFromStartIndex);
        }
      } while (spans.length);

      return spansWithWords;
    },

    handleSolveAll() {
      const {
        currentStep: { bold, italic, underline }
      } = this;

      const section = bold || italic || underline;
      let typeFormat = null;
      if (bold) {
        typeFormat = 'bold';
      } else if (italic) {
        typeFormat = 'italic';
      } else if (underline) {
        typeFormat = 'underline';
      }

      const isWholeSequence = section.split(' ').length > 1;
      const allSpans = this.getAllSpansWithWords();

      allSpans.forEach(targetEl => {
        targetEl.classList.add('formatted');
        const spans = targetEl.querySelectorAll('span');
        spans.forEach((span, idx) => {
          const text = span.innerText;
          // strip characters if it's not a sequence
          if (!isWholeSequence && !text.match(/[,!?. "']/g)) {
            span.classList.add(typeFormat);
          } else if (isWholeSequence) {
            if (idx < spans.length - 1) {
              span.classList.add(typeFormat);
            } else if (!text.match(/ /g, '')) {
              span.classList.add(typeFormat);
            }
          }
        });
      });

      this.sndBell();

      TweenMax.to('.solve-all-button', 0.5, { autoAlpha: 0 });
      this.handleRoundEnd();
    },

    resetDocumentSelection() {
      document.getSelection().removeAllRanges();
    },

    shouldThrottle() {
      const penultimateIndex = this.currentIndex === this.currentStep.answer.length - 1;
      return this.usingExternalKeyboard && penultimateIndex;
    },

    getSpanInnerText(span) {
      return span && span.innerText
        ? this.removeNewLineCharacters(span.innerText)
            .toLowerCase()
            .trim()
        : '';
    },

    removeNewLineCharacters(string) {
      return string.replace(/(\r\n|\n|\r)/gm, '');
    }
  }
};
</script>

<style lang="scss">
.fancy-formatting-container {
  display: flex;
  flex-flow: row nowrap;
  height: 100%;
  justify-content: center;
}

.fancy-image {
  img {
    height: 100%;
    max-height: 380px;
    max-width: 360px;
    width: 100%;
  }

  figure {
    margin-inline-start: 10px;
    margin-inline-end: 10px;
  }
}

.fancy-formatting-instructions-container {
  display: flex;
  flex-flow: column nowrap;
  padding: 1em 1em 0 0;
  width: 60%;
}

.fancy-formatting-instructions-container.isMobile {
  padding: 1em 2em 0 0;
}

.format-buttons-container {
  padding: 0.6em;

  .title {
    margin-bottom: 0.3em;
  }

  button,
  span {
    font-family: 'hwtfutura-semibold';

    .solve-all-button {
      height: 33px;
      min-width: 77px;
    }
  }
}

.format-section {
  margin: 10px 0px;
}

.format-section-background {
  height: 97px;
  width: 100%;

  @media (max-width: 691px) {
    background-position: center;
    background-size: 500px 97px;
  }
}

.format-buttons {
  .format-button {
    background-color: #ffffff;
    cursor: pointer;
    margin-right: 1em;

    &.disabled {
      opacity: 0.5;
      cursor: default;
      pointer-events: none;
    }
  }

  .format-button.bold,
  .format-button.italic,
  .format-button.underline {
    width: 55px;
    height: 55px;
  }

  .solve-all-button {
    align-self: center;
    background-color: black;
    text-align: center;
    padding: 6px 5px;
    cursor: pointer;
    border-radius: 6px;
    visibility: hidden;

    span {
      font-size: 16px;
      user-select: none;
      color: white;
    }
  }
}

.format-text-display {
  list-style: none;
  padding-left: 0;
  padding-inline-start: 0;
  margin-block-start: 0.4em;
  margin-block-end: 0 p {
    font-size: 19px;
  }

  .selectable p {
    -webkit-user-select: text; /* Chrome 49+ */
    -moz-user-select: text; /* Firefox 43+ */
    -ms-user-select: text; /* No support yet */
    user-select: text; /* Likely future */

    span {
      font-size: 19px;
    }

    span::-webkit-selection {
      background-color: black;
      color: white;
    }

    span::-moz-selection {
      background-color: black;
      color: white;
    }

    span::selection {
      background-color: black;
      color: white;
    }
  }

  .bold {
    font-family: hwtfutura-bold;
  }

  .italic {
    font-style: italic;
  }

  .underline {
    text-decoration: underline;
  }
}

.chrome-os,
.tablet {
  .fancy-image {
    img {
      max-height: 300px;
      max-width: 280px;
    }
  }
}
</style>
